<script setup>
import IndividualCalls from "@/components/LTE/IndividualCalls.vue";
import GroupCalls from "@/components/LTE/GroupCalls.vue";
import {useGroupCallsStore} from "@/stores/groupCallsStore";
import {useCallsStore} from "@/stores/callsStore";
import {useContactsStore} from "@/stores/contactsStore";
import {useICPStore} from "@/stores/icpStore";
import {onMounted} from "vue";
import {notifyError} from "@/services/toastService";

const groupCallsStore = useGroupCallsStore();
const callsStore = useCallsStore();
const contactsStore = useContactsStore();

let ICP = useICPStore().ICP;

onMounted(() => {
  contactsStore.selectedHandheld = [];
  contactsStore.selectedTrains = [];
  contactsStore.selectedPhoneContacts = [];

  //handle here the case when getting the floor of a group call fails
  ICP.dispatch.event.register({
    "eventType": "GroupCallNotify",
    "eventName": "OnTalkingGroupCallPTTFailure",
    "callback": function () {
      const pttButton = document.querySelector('#controlls button:nth-child(3)');
      if (pttButton) {
        pttButton.classList.toggle('bg-green');
        pttButton.classList.toggle('text-white');
        notifyError("Failed to get the floor in group call. Try again when call returns to 'Idle'.");

      }
    }
  });
});

function hangupCall() {
  if (callsStore.selectedCall.length > 0) {
    if (callsStore.selectedCall[0].status === 'Ringing' && callsStore.selectedCall[0].direction === 'Incoming') {
      ICP.dispatch.voice.reject({
        "cid": callsStore.selectedCall[0].cid,
        "callback": () => {
          callsStore.rejectCall(callsStore.selectedCall[0]);
        }
      })
    } else {
      ICP.dispatch.voice.release({
        "cid": callsStore.selectedCall[0].cid,
        "callback": () => {
        }
      })
    }

  }
}

function answerCall() {
  if (callsStore.selectedCall.length > 0) {
    ICP.dispatch.voice.answer({
      "cid": callsStore.selectedCall[0].cid.toString(),
      "callback": () => {
      }
    })
  }
}

function talkInGroup(src, isTalking) {
  if (isTalking) {
    if (groupCallsStore.getGroupByIsdn(groupCallsStore.selectedCall[0])?.emergency) {
      ICP.dispatch.group.pttTalkingGroupEmergencyCall({
        "grpid": groupCallsStore.getActiveGroupIsdn,
        "callback": function () {
        }
      })
    } else {
      ICP.dispatch.group.pttTalkingGroup({
        "grpid": groupCallsStore.getActiveGroupIsdn,
        "type": "",
        "isEncypt": "",
        "source": "",
        "fmt": {
          "isMute": "",
          "video_fmt": "",
          "video_type": "",
          "audio_type": "",
          "amr_mode": ""
        },
        "callback": function () {
        }
      })
    }

  } else {
    ICP.dispatch.group.pttreleaseTalkingGroup({
      "grpid": groupCallsStore.getActiveGroupIsdn,
      "callback": function () {
      }
    })
  }
  src.classList.toggle('bg-green', isTalking);
  src.classList.toggle('text-white', isTalking);
}

function putInCommunication() {
  // check if the only selected contacts are two trains, then start t2t, else start pure dynamic group call
  const trains = contactsStore.selectedTrains;
  const handheld = contactsStore.selectedHandheld;
  const phoneContacts = contactsStore.selectedPhoneContacts;
  var addList = [];
  if (trains.length === 2 && handheld.length === 0 && phoneContacts.length === 0) {
    addList.push(trains[0].value.isdn);
    addList.push(trains[1].value.isdn);
    groupCallsStore.realignT2TGroup(addList)
  } else if (contactsStore.getAllSelected.length > 1) {
    for (var i = 0; i < contactsStore.selectedTrains.length; i++) {
      addList.push(contactsStore.selectedTrains[i].value.isdn);
    }
    for (var j = 0; j < contactsStore.selectedHandheld.length; j++) {
      addList.push(contactsStore.selectedHandheld[j].value.isdn);
    }
    for (var k = 0; k < contactsStore.selectedPhoneContacts.length; k++) {
      addList.push(contactsStore.selectedPhoneContacts[k].isdn);
    }
    groupCallsStore.realignPatchGroup(addList)
  }
}

function startCall() {
  const isdn = contactsStore.getAllSelected[0].value.isdn;

  ICP.dispatch.voice.dial({
    "to": isdn,
    "mikey": "",
    "callback": () => {
    }
  })
}

function startEmergencyCall() {
  const isdn = contactsStore.getAllSelected[0].value.isdn;

  ICP.dispatch.voice.intercept({
    "to": isdn,
    "callback": () => {
    }
  })
}

</script>

<template>
  <v-col class="d-flex flex-column h-100 pa-0">
    <IndividualCalls/>
    <GroupCalls/>
    <div id="controlls" class="border-t w-100 d-flex justify-space-evenly align-center">
      <v-btn
          :disabled="contactsStore.getAllSelected.length !== 1"
          class="bg-red text-white" @click="startEmergencyCall()">
        Emergency Call
      </v-btn>
      <v-btn
          :class="{'bg-green': groupCallsStore.putInCommGreen, 'text-white': groupCallsStore.putInCommGreen}"
          :disabled="contactsStore.getAllSelected.length < 2"
          @click="putInCommunication()">
        Put in communication
      </v-btn>
      <v-btn
          :disabled="groupCallsStore.pttDisabled"
          class="ptt-btn d-flex flex-column align-center"
          icon
          size="large"
          @mousedown="talkInGroup($event.currentTarget, true)"
          @mouseleave="talkInGroup($event.currentTarget, false)"
          @mouseup="talkInGroup($event.currentTarget, false)"
      >
        <div class="d-flex flex-column align-center">
          <v-icon size="34">mdi-microphone</v-icon>
          <span style="font-size: 0.75rem; margin-top: 1px;">PTT</span>
        </div>
      </v-btn>
      <v-btn
          :disabled="callsStore.selectedCall.length===0"
          @click="hangupCall">
        Hang up
      </v-btn>
      <v-btn
          v-if="contactsStore.selectedPhoneContacts.length === 0 || contactsStore.selectedTrains.length + contactsStore.selectedHandheld !== 1"
          :disabled="callsStore.selectedCall[0]?.status !== 'Ringing' || callsStore.selectedCall[0]?.direction === 'Outgoing'"
          @click="answerCall">
        Answer
      </v-btn>
      <v-btn
          v-if="contactsStore.getAllSelected.length === 1"
          @click="startCall()">
        Call
      </v-btn>
    </div>
  </v-col>

</template>

<style scoped>

#controlls {
  height: 70px;
  background-color: darkgray;
}


.ptt-btn {
  border-radius: 50% !important;
  width: 55px;
  height: 55px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  margin-top: 6px;
  margin-bottom: 6px;
}

</style>
